import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { UUID } from 'crypto'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useToken } from '../hooks/useToken'
import { useGenerateProfile } from '../hooks/useGenerateProfile'
import { useAppendDescriptionAndGenerate } from '../hooks/useAppendDescriptionAndGenerate'
import ProgressScreen from '../components/ProgressScreen'
import AssessmentGate from '../components/AssessmentGate'
import FailedPagesNotice, { failedPages, canRetry } from '../components/FailedPagesNotice'
import SlowPagesNotice from '../components/SlowPagesNotice'
import { useBatchScrape, type PageEntry } from '../hooks/useBatchScrape'
import type { ProfileAssessment } from '@/models/Orgs'
import { useSSEProgress } from '../hooks/useSSEProgress'
import { useTaskContext } from '@/utils/tasks/TaskContext'
import { useSuppressTaskToasts } from '@/components/TaskToaster'

const HELP_TEXTS = [
  'We\'re analysing your documents, website, and other information to create a comprehensive profile of your organisation. This usually takes a minute or two.',
]

const WAITING_HELP_TEXTS = [
  'We\'re still reading the webpages you added. Your profile will start generating as soon as they\'re in.',
]

const PROGRESS_INTERVAL_MS = 1000
const PROGRESS_DURATION_MS = 30000
const PROGRESS_INCREMENT = 90 / (PROGRESS_DURATION_MS / PROGRESS_INTERVAL_MS)

// How long to wait for pages still being read before offering to go on without
// them. A crawl has no overall time limit and a queued one never expires, so the
// user is asked rather than held. The crawl worker's profile update needs an
// existing profile, so a page that lands while the first one generates misses it.
const INGEST_WAIT_MS = 5 * 60 * 1000

// What app/worker.py stamps on a crawl the moment it claims the job, before the
// crawler has reported anything of its own.
const CRAWL_ENQUEUED_PROGRESS = 5

export default function ProfileGenerating() {
  const {
    orgUuid, goNext, goBack, goToStep, setHideNav,
    staleFrom, setStaleFrom, profileGenerated, setProfileGenerated,
  } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const queryClient = useQueryClient()
  const batchScrape = useBatchScrape(orgUuid ?? '', getToken)

  const generateProfile = useGenerateProfile()
  const appendAndGenerate = useAppendDescriptionAndGenerate()

  // Refs for mutation functions to avoid stale closures
  const generateProfileRef = useRef(generateProfile)
  generateProfileRef.current = generateProfile
  const appendAndGenerateRef = useRef(appendAndGenerate)
  appendAndGenerateRef.current = appendAndGenerate
  const staleFromRef = useRef(staleFrom)
  staleFromRef.current = staleFrom

  // Abort guard — prevents post-unmount goNext/persist calls
  const unmountedRef = useRef(false)
  useEffect(() => () => { unmountedRef.current = true }, [])

  // Fake progress
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [profileJobId, setProfileJobId] = useState<string | null>(null)
  const [profileJobToken, setProfileJobToken] = useState<string | null>(null)
  const startedRef = useRef(false)
  const [started, setStarted] = useState(false)
  const [waitedOutIngest, setWaitedOutIngest] = useState(false)
  // Bumped by Try again. When pages from an earlier job are still processing,
  // ingestPending never changes, so the wait would otherwise get no new timer.
  const [waitRound, setWaitRound] = useState(0)
  // The user chose to go on without pages still being read.
  const [skippedSlowPages, setSkippedSlowPages] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Gate state
  const [assessment, setAssessment] = useState<ProfileAssessment | null>(null)
  const [showGate, setShowGate] = useState(false)
  const [gateInputs, setGateInputs] = useState<Record<number, string>>({})
  const contextAppendedRef = useRef(false)

  const startProgress = useCallback(() => {
    setProgress(0)
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          if (intervalRef.current) clearInterval(intervalRef.current)
          return prev
        }
        return prev + PROGRESS_INCREMENT
      })
    }, PROGRESS_INTERVAL_MS)
  }, [])

  const stopProgress = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
  }, [])

  const completeProfileGeneration = useCallback((result: any) => {
    if (unmountedRef.current) return
    stopProgress()
    if (result?.profile) {
      setProfileGenerated(true)
      if (staleFromRef.current !== null && staleFromRef.current <= STEPS.PROFILE_GENERATING) {
        setStaleFrom(STEPS.SCAN_RUNNING)
      }
      setProgress(100)
      // Profile review reads the org from the query cache, which holds a copy
      // fetched before this profile existed. Review seeds its editor from the
      // first profile it sees, so a stale copy would stick: empty on a first
      // run, the previous profile on a regenerate.
      void queryClient
        .refetchQueries({ queryKey: ['currentOrgs'], type: 'all' })
        .then(() => {
          if (!unmountedRef.current) goNext()
        })
    } else if (result?.assessment) {
      // Still bad quality — show gate again with updated suggestions
      setAssessment(result.assessment)
      setShowGate(true)
      setGateInputs({})
    }
  }, [goNext, setProfileGenerated, setStaleFrom, stopProgress, queryClient])

  const profileProgress = useSSEProgress({
    type: 'profile',
    jobId: profileJobId,
    token: profileJobToken,
    onError: () => {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    },
  })

  useEffect(() => {
    if (profileProgress.status !== 'completed') return
    completeProfileGeneration(profileProgress.data)
    setProfileJobId(null)
  }, [profileProgress.status, profileProgress.data, completeProfileGeneration])

  const startProfileJob = useCallback(async (t: string, additionalText?: string) => {
    const result = additionalText
      ? await appendAndGenerateRef.current.mutateAsync({
          token: t,
          orgUuid: orgUuid as UUID,
          additionalText,
        })
      : await generateProfileRef.current.mutateAsync({
          token: t,
          orgUuid: orgUuid as UUID,
        })
    setProfileJobToken(t)
    setProfileJobId(result.job_id)
  }, [orgUuid])

  const runGeneration = useCallback(async () => {
    try {
      const t = await getToken()

      // Append additional context and generate in one call (only once)
      if (wizardData.additionalContext && orgUuid && !contextAppendedRef.current) {
        contextAppendedRef.current = true
        await startProfileJob(t, wizardData.additionalContext)
        return
      }

      await startProfileJob(t)
    } catch {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    }
  }, [getToken, wizardData.additionalContext, orgUuid, startProfileJob, stopProgress])

  // Any page still mid-crawl. OnboardingUploadWatcher clears 'processing' as
  // each job lands, so this settles on its own.
  const ingestPending = wizardData.pages.some(p => p.status === 'processing')

  // While we're the screen showing that wait, the toaster saying the same thing
  // is noise — and its "you can keep going" is the opposite of what's happening.
  useSuppressTaskToasts(ingestPending && !started)

  // Real progress from the crawl jobs the pages are waiting on. A crawl parks at
  // the enqueue floor while it discovers links and only starts reporting once
  // pages land, so below that we show no number at all — the spinner carries it.
  // A percentage that cannot move for two minutes reads as broken.
  const { tasks } = useTaskContext()
  const ingestProgress = useMemo(() => {
    const jobIds = new Set(
      wizardData.pages
        .filter(p => p.status === 'processing' && p.jobId)
        .map(p => p.jobId)
    )
    const live = Object.values(tasks).filter(t => jobIds.has(t.id))
    if (live.length === 0) return undefined
    const total = live.reduce((sum, t) => sum + (t.progress || 0), 0)
    const average = Math.round(total / live.length)
    return average > CRAWL_ENQUEUED_PROGRESS ? average : undefined
  }, [tasks, wizardData.pages])

  // Pages that never made it in. Generation holds until the user retries them
  // or chooses to go on without them.
  const failed = useMemo(() => failedPages(wizardData.pages), [wizardData.pages])
  const [continuedWithout, setContinuedWithout] = useState(false)
  const holdForFailures = failed.length > 0 && !continuedWithout

  useEffect(() => {
    if (!ingestPending) return
    const timer = setTimeout(() => setWaitedOutIngest(true), INGEST_WAIT_MS)
    return () => clearTimeout(timer)
  }, [ingestPending, waitRound])

  // Fire once, as soon as nothing is still being ingested
  useEffect(() => {
    if (startedRef.current || !orgUuid) return
    if (ingestPending && !skippedSlowPages) return
    if (holdForFailures) return
    startedRef.current = true
    setStarted(true)
    startProgress()
    runGeneration()

    return () => { stopProgress() }
  }, [orgUuid, ingestPending, skippedSlowPages, holdForFailures])

  // Hide shell nav during progress, show during gate
  useEffect(() => {
    setHideNav(!showGate)
    return () => setHideNav(false)
  }, [showGate, setHideNav])

  // Submit the gate answers and re-generate. AssessmentGate drives the shell's
  // single forward button and calls this once the user advances past the last
  // question; we own the resulting navigation. Generation runs through the
  // async job queue, so completion is handled by the SSE progress effect.
  const handleGateSubmit = useCallback(async (combinedText: string) => {
    // Reset to progress mode
    setShowGate(false)
    setAssessment(null)

    try {
      const t = await getToken()
      startProgress()

      await startProfileJob(t, combinedText)
    } catch {
      stopProgress()
      setError('Something went wrong while generating your profile. Please try again.')
    }
  }, [getToken, startProgress, stopProgress, startProfileJob])

  // Error retry (network/server errors, not quality retries)
  const handleRetry = useCallback(() => {
    setError(null)
    setProgress(0)
    // startedRef stays set: the start effect would otherwise fire a second run.
    startProgress()
    runGeneration()
  }, [startProgress, runGeneration])

  const handleRetryPages = useCallback(async () => {
    const urls = new Set(failed.filter(canRetry).map(p => p.url))
    setWizardData(prev => ({
      pages: prev.pages.map(p => (urls.has(p.url) ? { ...p, retried: true } : p)),
    }))
    // A fresh wait for the resubmitted pages, not what is left of the first.
    setWaitedOutIngest(false)
    setSkippedSlowPages(false)
    setWaitRound(round => round + 1)
    await batchScrape.submit([...urls], updater => {
      setWizardData(prev => ({
        pages: typeof updater === 'function'
          ? updater(prev.pages)
          : (updater as PageEntry[]),
      }))
    })
  }, [failed, setWizardData, batchScrape])

  // Gate mode — render assessment form (shell renders nav buttons)
  if (showGate && assessment) {
    return (
      <AssessmentGate
        heading="We need a bit more information"
        items={assessment.Suggestions.slice(0, 3).map(s => ({ label: s }))}
        quality={assessment.Quality}
        inputs={gateInputs}
        onInputChange={(i, v) => setGateInputs(prev => ({ ...prev, [i]: v }))}
        onSubmit={handleGateSubmit}
      />
    )
  }

  if (!started && ingestPending && waitedOutIngest && !skippedSlowPages) {
    return (
      <SlowPagesNotice
        pages={wizardData.pages.filter(p => p.status === 'processing')}
        onKeepWaiting={() => {
          setWaitedOutIngest(false)
          setWaitRound(round => round + 1)
        }}
        onContinue={() => setSkippedSlowPages(true)}
      />
    )
  }

  const ingestSettled = !ingestPending || skippedSlowPages
  if (!started && ingestSettled && holdForFailures) {
    return (
      <FailedPagesNotice
        pages={failed}
        allFailed={failed.length === wizardData.pages.length}
        onRetry={handleRetryPages}
        onContinue={() => setContinuedWithout(true)}
        onBack={() => goToStep(STEPS.UPLOAD_WEBPAGES)}
      />
    )
  }

  // Progress mode
  return (
    <ProgressScreen
      statusText={started
        ? 'Generating your organisation profile...'
        : 'Reading your website pages...'}
      helpTexts={started ? HELP_TEXTS : WAITING_HELP_TEXTS}
      progress={started ? profileProgress.progress || progress : ingestProgress}
      error={error}
      onRetry={handleRetry}
      onBack={() => goBack()}
    />
  )
}
